import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useUrlState } from '../../hooks/useUrlState';
import ConfirmDialog from '../common/ConfirmDialog';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import { useEntityTimeline } from '../../services/auditTimelineService';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Checkbox,
  ListItemText,
  Autocomplete,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Paper,
  createFilterOptions
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  SwapHoriz as TransferIcon,
  ArrowForward as ArrowIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  Check as CheckIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import api from '../../config/api';
import { useBrand } from '../../context/BrandContext';
import {
  getTransfers,
  getTransfer,
  shipTransfer,
  receiveTransfer,
  deleteTransfer,
  getWarehouses,
  formatNumber,
  formatDate
} from '../../services/inventoryService';
import { StatusBadge, PageHeader, ActionBar, EntityChip, Timeline } from '../ds';
import {
  Print as PrintIcon,
  ContentCopy as CopyIcon,
  CloudUpload as UploadIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { skippedCostRows, skippedCostRowsWarning } from '../../utils/cogsWriteFeedback';

// Warehouse pickers match on code, name, city AND country (Rishabh 2026-07-29: codes like
// "BT" -> "Omnipack PL" aren't memorable, so typing the name must find the warehouse). The
// getOptionLabel stays the code (for the chip/input), only the FILTER key is widened.
const warehouseFilter = createFilterOptions({
  stringify: (w) => `${w?.code || ''} ${w?.name || ''} ${w?.city || ''} ${w?.country || ''}`,
});

const STATUS_COLORS = {
  draft: 'default',
  in_transit: 'warning',
  completed: 'success',
  cancelled: 'error'
};

// po_number holds ONE PO for UI-entered transfers but SEVERAL space-joined POs
// on some backfilled rows (TRF-1077: "EF-26453 EF-31428 EF-14044 EF-14045").
// Always render/link per individual PO — a combined ?q= search matches nothing.
const splitPoNumbers = (s) => String(s || '').trim().split(/\s+/).filter(Boolean);

// DISPLAY-ONLY relabel (Rishabh): transfers are created with DB status='draft'
// (service.js:907) and ALL status logic keeps comparing 'draft'. Operators asked
// to SEE this state as "Open" — there is no separate 'open' status. Only the
// human-facing label is changed here; the underlying value is untouched.
const STATUS_STEPS = ['Open', 'In Transit', 'Completed'];

// Only allow http(s) in drive_link hrefs — blocks stored javascript:/data: URIs
// (React renders href as-is, so an attacker-set scheme would execute on click).
// new URL() is the WHATWG parser, so case/whitespace/tab/newline obfuscation
// normalizes the same way the browser navigates → no parser-differential bypass.
// Returns the normalized URL when safe, null otherwise (caller hides the link).
const safeHref = (u) => {
  try {
    const p = new URL(u);
    return (p.protocol === 'https:' || p.protocol === 'http:') ? p.href : null;
  } catch {
    return null;
  }
};

// Surfaces both { error: '...' } and express-validator { errors: [...] }
// responses cleanly. Without this, axios's "Request failed with status code N"
// is what users see for validator rejections.
const extractApiError = (e, fallback = 'Request failed') => {
  const d = e?.response?.data;
  if (d?.error) return d.error;
  if (Array.isArray(d?.errors) && d.errors.length) {
    return d.errors.map(x => x.path ? `${x.msg} (${x.path})` : x.msg).join('; ');
  }
  return e?.message || fallback;
};

function Transfers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Remember the list's filter query-string so the detail's Back arrow returns
  // to the SAME filtered view. history(-1) is wrong here: create/edit push an
  // intermediate form entry between the list and the detail, so "back" bounced
  // to the edit form instead of the list (Rishabh 2026-07-01). Persisting the
  // list URL makes Back deterministic regardless of how the detail was reached.
  useEffect(() => {
    if (!id) {
      try { sessionStorage.setItem('scm.transfersListSearch', window.location.search || ''); } catch (_) { /* private mode */ }
    }
  }, [id, searchParams]);

  const [loading, setLoading] = useState(true);
  // The id whose transfer load last finished (answered or failed). The single transfer used to share `loading` with the
  // list, so on a transfer opened by URL whichever answer landed first ended the spinner: the list (or an empty list)
  // showed before the transfer arrived (DT-1113). "Still loading" is read in render (URL id not settled yet), not from a
  // flag set by the load effect, which runs one render after the URL id changes.
  const [detailSettledId, setDetailSettledId] = useState(null);
  // Only the newest transfer load may apply its answer: an older one landing late (the user moved to another transfer,
  // or back to the list) would show the wrong transfer, or leave the new one spinning forever (DT-1113 send-back).
  const detailRequestRef = useRef(0);
  // Back on the list: forget it, so reopening a transfer whose earlier load failed spins again while it reloads.
  useEffect(() => {
    if (!id) {
      detailRequestRef.current += 1;
      setDetailSettledId(null);
    }
  }, [id]);
  // initialLoaded gates the full-page spinner so subsequent filter-driven
  // refetches don't unmount the entire UI (which closes any open Select/
  // Autocomplete dropdown — Rishabh complaint 2026-05-22 "ticking one
  // checkbox closes the menu and forces re-open per option").
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Non-blocking: the receive completed but the server skipped some destination cost rows (cost_rows_skipped, W20).
  // Also carried here from the transfer form's Save & Close (navigate state).
  const [warning, setWarning] = useState(() => location.state?.costWarning || null);

  // Data
  const [transfers, setTransfers] = useState([]);
  // Server-side pagination: the list previously hard-fetched only the latest 100
  // transfers with no way to reach the rest (Rishabh 2026-07-23 — "only 100 rows,
  // are there only 100?"). Backend already supports limit+offset and returns total.
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  // Activity timeline for this transfer (audit_log). Called unconditionally
  // (rules-of-hooks); no-ops until a transfer is selected. Mirrors the PO panel.
  const { events: transferTimelineEvents, loading: transferTimelineLoading } = useEntityTimeline(
    // Semantic entity_type (not the '_route' one POs use): it logs 'create' with
    // the real transfer id + actor, so the trail shows who created the transfer;
    // the route-level create carries an empty entity_id and would be missed.
    'warehouse_transfer',
    selectedTransfer?.id || null
  );
  const [warehouses, setWarehouses] = useState([]);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Shared MUI confirm. This file used to carry its own copy of a confirm
  // dialog; it rendered differently (maxWidth sm, no severity icon) from the
  // app-wide one used by 25+ other screens, so a Transfers confirm and a PO
  // confirm looked like two different products. Now there is one primitive.
  const { confirmDialogProps, confirm } = useConfirmDialog();

  // Attachments (PDF/CSV/XLSX/PNG/JPG) on the open transfer
  const [attachments, setAttachments] = useState([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);

  const loadAttachments = async (transferId) => {
    try {
      const r = await api.get(`/api/warehouse-transfers/${transferId}/attachments`);
      setAttachments(r.data?.attachments || []);
    } catch {
      setAttachments([]); // non-fatal — the card just shows empty
    }
  };

  const handleUploadAttachment = async (transferId, file) => {
    if (!file) return;
    setAttachmentBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/api/warehouse-transfers/${transferId}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(`Attached ${file.name}`);
      await loadAttachments(transferId);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleDownloadAttachment = async (transferId, filename) => {
    try {
      const r = await api.get(
        `/api/warehouse-transfers/${transferId}/attachments/${encodeURIComponent(filename)}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleDeleteAttachment = async (transferId, filename) => {
    const ok = await confirm({
      title: 'Delete this attachment?',
      message: `"${filename}" will be removed from this transfer. You can upload it again afterwards.`,
      confirmText: 'Delete attachment',
      severity: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/api/warehouse-transfers/${transferId}/attachments/${encodeURIComponent(filename)}`);
      await loadAttachments(transferId);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  useEffect(() => {
    if (id) loadAttachments(id);
    else setAttachments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const recentlyDeletedIds = useRef(new Set());

  // Bulk-select state (Rishabh request 2026-05-22 — there were 50+ cancelled
  // transfers piling up). selectedIds: Set<number> of transfer ids currently
  // ticked in the header checkbox column. Cleared on every list refetch.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  // List-view URL-bound filter state. statusFilter stores a comma-separated
  // list of selected statuses ('draft,in_transit,completed' etc.) — empty
  // string means "all". Matches the PO module's multi-checkbox convention.
  // DEFAULT = the OPEN/active transfers ('draft' shows as "Open" + in_transit) so
  // a fresh open lands on what needs action, not a wall of completed/cancelled ones
  // (Rishabh 2026-07-27, "only Open Transfers first, same as PO/retail, system-wide").
  // Only the first load with no ?status= URL param gets this; clearing the filter
  // still shows all.
  const [statusFilter, setStatusFilter] = useUrlState('status', 'draft,in_transit');
  const [fromWhFilter, setFromWhFilter] = useUrlState('from_wh', '');
  const [toWhFilter, setToWhFilter] = useUrlState('to_wh', '');
  // Brand multi-select ('0' = Unassigned — cross-brand/backfilled transfers).
  // Mirrors the PO module's Brand filter (Rishabh request 2026-07-03).
  const [brandFilter, setBrandFilter] = useUrlState('brand', '');
  const { brands } = useBrand();
  const [searchTerm, setSearchTerm] = useUrlState('q', '');
  const [dateFromFilter, setDateFromFilter] = useUrlState('from', '');
  const [dateToFilter, setDateToFilter] = useUrlState('to', '');
  // Local typing buffer — 400ms-debounced into searchTerm to avoid one fetch
  // per keystroke (same fix Rishabh asked for on the PO module 2026-05-22).
  const [searchInput, setSearchInput] = useState(searchTerm || '');

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchTerm) setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    // Always populate the list — guarantees the page renders something useful even
    // if a deep-link to a deleted/invalid transfer fails. (Prior behavior: when the
    // detail load failed, the else branch was skipped → transfers state stayed empty
    // → user saw "Failed to load transfer details" stuck state.)
    loadTransfers();
    if (id) {
      loadTransferDetails(id);
    }
    loadWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, statusFilter, fromWhFilter, toWhFilter, brandFilter, searchTerm, dateFromFilter, dateToFilter, page, rowsPerPage]);

  // Any filter/search change resets to the first page so the user never lands on
  // an out-of-range page (e.g. page 5 of a set that now has 1 page).
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, fromWhFilter, toWhFilter, brandFilter, searchTerm, dateFromFilter, dateToFilter]);

  const loadTransfers = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { limit: rowsPerPage, offset: page * rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (fromWhFilter) params.from_warehouse_code = fromWhFilter;
      if (toWhFilter) params.to_warehouse_code = toWhFilter;
      if (brandFilter) params.brand_id = brandFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFromFilter) params.date_from = dateFromFilter;
      if (dateToFilter) params.date_to = dateToFilter;
      const res = await getTransfers(params);
      // Backend returns { success, transfers, total } not { data }
      const all = res?.transfers || res?.data || [];
      setTotalCount(Number(res?.total) || 0);
      // Filter out recently deleted transfers (ClickHouse DELETE mutations are async)
      setTransfers(recentlyDeletedIds.current.size > 0
        ? all.filter(t => !recentlyDeletedIds.current.has(t.id))
        : all);
      // Reset bulk selection whenever the underlying list refetches — keeping
      // stale ids ticked could mean a delete-selected click after a filter
      // change deletes rows the user can no longer see in the table.
      setSelectedIds(new Set());
      setInitialLoaded(true);
    } catch (err) {
      console.error('Failed to load transfers:', err);
      setError('Failed to load transfers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Build the current filter object — used both for the listEndpoint and for
  // the bulk-delete-by-filter endpoint so they stay in lockstep.
  const currentFilters = () => ({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(fromWhFilter ? { from_warehouse_code: fromWhFilter } : {}),
    ...(toWhFilter ? { to_warehouse_code: toWhFilter } : {}),
    ...(brandFilter ? { brand_id: brandFilter } : {}),
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(dateFromFilter ? { date_from: dateFromFilter } : {}),
    ...(dateToFilter ? { date_to: dateToFilter } : {}),
  });

  // Bulk-select handlers ------------------------------------------------
  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set((transfers || []).map(t => Number(t.id))));
    } else {
      setSelectedIds(new Set());
    }
  };
  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(Number(id))) next.delete(Number(id)); else next.add(Number(id));
      return next;
    });
  };

  const bulkCancelSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Cancel ${ids.length} transfer${ids.length === 1 ? '' : 's'}?`,
      message:
        'Only draft transfers can be cancelled — anything else in your selection is skipped. ' +
        'To cancel a transfer that has already shipped, reverse it via Edit first.\n\n' +
        'Cancelled transfers stay in the list marked Cancelled, and can be removed later with "Delete all cancelled".',
      confirmText: 'Cancel transfers',
      cancelText: 'Keep them',
      severity: 'warning',
    });
    if (!ok) return;
    await doBulkCancelSelected(ids);
  };

  const doBulkCancelSelected = async (ids) => {
    setBulkBusy(true);
    setError(null);
    try {
      const r = await api.post(
        `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/warehouse-transfers/bulk-cancel`,
        { ids }
      );
      const { cancelled, failed, errors } = r.data || {};
      setSuccess(
        `Cancelled ${cancelled || 0} transfer(s)` +
        (failed ? ` — ${failed} skipped${errors?.length ? `: ${errors.slice(0, 3).map(e => `#${e.id} (${e.error})`).join('; ')}${errors.length > 3 ? '…' : ''}` : ''}` : '')
      );
      setSelectedIds(new Set());
      await loadTransfers();
    } catch (err) {
      setError(extractApiError(err) || 'Bulk cancel failed');
    } finally { setBulkBusy(false); }
  };

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: `Delete ${ids.length} transfer${ids.length === 1 ? '' : 's'}?`,
      message:
        'Any transfer that has already shipped is fully reversed (ship and receive undone) before it is deleted, so stock returns to where it started.\n\n'
        + 'This cannot be undone.',
      confirmText: 'Delete transfers',
      severity: 'danger',
    });
    if (!ok) return;
    await doBulkDeleteSelected(ids);
  };

  const doBulkDeleteSelected = async (ids) => {
    setBulkBusy(true);
    setError(null);
    try {
      const r = await api.post(
        `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/warehouse-transfers/bulk-delete`,
        { ids }
      );
      const { deleted, failed, errors } = r.data || {};
      // Mark deleted ids as recently-deleted so the optimistic filter survives
      // a stale read while ClickHouse mutates.
      ids.forEach(id => recentlyDeletedIds.current.add(Number(id)));
      setSuccess(
        `Deleted ${deleted || 0} transfer(s)` +
        (failed ? ` — ${failed} failed${errors?.length ? `: ${errors.slice(0, 3).map(e => `#${e.id} (${e.error})`).join('; ')}${errors.length > 3 ? '…' : ''}` : ''}` : '')
      );
      await loadTransfers();
    } catch (err) {
      setError(extractApiError(err) || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };

  const bulkDeleteAllCancelled = async () => {
    // Use the all_with_status path so backend resolves the id list under the
    // SAME filters the operator currently has applied. Operator can scope to
    // a specific From/To/date range and only those cancelled rows die.
    const filters = currentFilters();
    // Drop the status from filters — backend overrides with all_with_status.
    delete filters.status;
    const filterCount = Object.keys(filters).length;
    const ok = await confirm({
      title: 'Delete all cancelled transfers?',
      message: filterCount > 0
        ? 'Every cancelled transfer matching your current filters will be deleted. Your warehouse, date and search filters apply; the status filter is ignored.\n\nThis cannot be undone.'
        : 'Every cancelled transfer in the system will be deleted — you have no filters applied.\n\nThis cannot be undone.',
      confirmText: 'Delete cancelled',
      severity: 'danger',
    });
    if (!ok) return;
    await doBulkDeleteAllCancelled(filters);
  };

  const doBulkDeleteAllCancelled = async (filters) => {
    setBulkBusy(true);
    setError(null);
    try {
      const r = await api.post(
        `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/warehouse-transfers/bulk-delete`,
        { all_with_status: 'cancelled', filters }
      );
      const { deleted, failed, errors } = r.data || {};
      setSuccess(
        `Deleted ${deleted || 0} cancelled transfer(s)` +
        (failed ? ` — ${failed} failed${errors?.length ? `: ${errors.slice(0, 3).map(e => `#${e.id}`).join(', ')}…` : ''}` : '')
      );
      await loadTransfers();
    } catch (err) {
      setError(extractApiError(err) || 'Bulk delete failed');
    } finally { setBulkBusy(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const params = currentFilters();
      const resp = await api.get(
        `${process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'}/api/warehouse-transfers/export.csv`,
        { params, responseType: 'blob' }
      );
      let filename = `warehouse-transfers-${new Date().toISOString().slice(0, 10)}.csv`;
      const cd = resp.headers?.['content-disposition'];
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess(`Exported as ${filename}`);
    } catch (err) {
      setError(extractApiError(err) || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const loadTransferDetails = async (transferId) => {
    const request = ++detailRequestRef.current;
    const stale = () => request !== detailRequestRef.current;
    setError(null);

    try {
      const res = await getTransfer(transferId);
      if (stale()) return;
      // Backend returns { success, transfer } not { data }
      setSelectedTransfer(res?.transfer || res?.data);
      setInitialLoaded(true);
    } catch (err) {
      if (stale()) return;
      console.error('Failed to load transfer details:', err);
      // Stale/invalid deep-link: surface a clearer message and bounce back to the list URL
      // so the next render no longer attempts to fetch the missing detail. The list itself
      // is loaded by the useEffect above, so the page recovers gracefully.
      const status = err?.response?.status || err?.status;
      if (status === 404) {
        setError(`Transfer #${transferId} not found. Showing the full list instead.`);
        // Preserve the filter the operator came from. A transient 404 (the brief
        // DELETE→INSERT window when a transfer is saved) must NOT bounce them to
        // the UNFILTERED list: landing on the bare list re-runs the sessionStorage
        // stash with an empty search and wipes their filter (Rishabh 2026-07-21 —
        // "edit anything and save, the filter gets removed").
        let savedSearch = '';
        try { savedSearch = sessionStorage.getItem('scm.transfersListSearch') || ''; } catch (_) { savedSearch = ''; }
        navigate(`/inventory/transfers${savedSearch}`, { replace: true });
      } else {
        setError('Failed to load transfer details. Please try again.');
      }
    } finally {
      if (!stale()) setDetailSettledId(String(transferId));
    }
  };

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses({ is_active: true });
      setWarehouses(res?.warehouses || res?.data || []);
    } catch (err) {
      console.error('Failed to load warehouses:', err);
    }
  };

  // Local currency formatter — Transfers.js doesn't import formatCurrency.
  // Mirrors the PO Shipments card's currency display (symbol-less, ccy code
  // suffix) so the landed-cost numbers read consistently.
  const formatShipCurrency = (value, ccy = 'USD') => {
    const n = Number(value) || 0;
    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy || ''}`.trim();
  };

  // Ship dialog state — captures actual quantity_shipped per line + ship metadata.
  // Defaults each line's qty_shipped to its qty_requested; operator can lower
  // it to record damage/loss between order and pickup.
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipTarget, setShipTarget] = useState(null);  // the full transfer object
  const [shipLines, setShipLines] = useState([]);       // [{item_id, sku, batch, requested, shipped}]
  const [shipMeta, setShipMeta] = useState({ shipped_date: '', tracking_number: '', carrier: '', cartons_shipped: '' });
  const [shipSaving, setShipSaving] = useState(false);

  const openShipDialog = async (transferId) => {
    try {
      setError(null);
      // Pull fresh items so quantity_requested is accurate even after an edit
      // Backend returns { success, transfer } (same unwrap as loadTransferDetails)
      const res = await getTransfer(transferId);
      const detail = res?.transfer || res?.data || res;
      const items = detail.items || [];
      setShipTarget(detail);
      setShipLines(items.map(it => ({
        item_id: it.id,
        sku: it.sku,
        batch_number: it.batch_number,
        quantity_requested: Number(it.quantity_requested) || 0,
        quantity_shipped: Number(it.quantity_requested) || 0,
      })));
      setShipMeta({
        shipped_date: new Date().toISOString().slice(0, 10),
        tracking_number: detail.tracking_number || '',
        carrier: detail.carrier || '',
        cartons_shipped: detail.cartons || ''
      });
      setError(null); setShipDialogOpen(true);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to open ship dialog');
    }
  };

  const submitShip = async () => {
    if (!shipTarget) return;
    try {
      setShipSaving(true);
      const body = {
        shipped_date: shipMeta.shipped_date || undefined,
        tracking_number: shipMeta.tracking_number || undefined,
        carrier: shipMeta.carrier || undefined,
        cartons_shipped: shipMeta.cartons_shipped ? Number(shipMeta.cartons_shipped) : undefined,
        // Only include items whose qty_shipped differs from requested OR are
        // 0 (partial / no-ship). Backend defaults to requested when omitted.
        items: shipLines
          .filter(l => Number(l.quantity_shipped) !== Number(l.quantity_requested))
          .map(l => ({ item_id: l.item_id, quantity_shipped: Number(l.quantity_shipped) || 0 }))
      };
      await shipTransfer(shipTarget.id, body);
      const shortfall = shipLines.filter(l => Number(l.quantity_shipped) < Number(l.quantity_requested));
      setSuccess(`Shipped transfer ${shipTarget.transfer_number}${shortfall.length ? ` — ${shortfall.length} line(s) shipped short` : ''}.`);
      setShipDialogOpen(false);
      // Optimistic: status moves to in_transit
      const now = new Date().toISOString();
      if (selectedTransfer && selectedTransfer.id === shipTarget.id) {
        setSelectedTransfer(prev => ({ ...prev, status: 'in_transit', shipped_at: now }));
      }
      setTransfers(prev => prev.map(t => t.id === shipTarget.id ? { ...t, status: 'in_transit' } : t));
    } catch (err) {
      setError(extractApiError(err) || 'Failed to ship');
    } finally { setShipSaving(false); }
  };

  // Wrapper preserved for existing call sites — opens the dialog instead of
  // shipping silently. The old "ship without confirmation" behavior is gone
  // intentionally; ship is a real workflow that needs operator input.
  const handleShipTransfer = (transferId) => openShipDialog(transferId);

  // Receive dialog state — captures per-line quantity_received + discrepancy reason.
  // Defaults each line's received qty to qty_shipped; if operator lowers it,
  // the reason field becomes required so the shortfall isn't silently lost.
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiveLines, setReceiveLines] = useState([]);
  const [receiveMeta, setReceiveMeta] = useState({ received_date: '', keep_in_transit: false });
  const [receiveSaving, setReceiveSaving] = useState(false);

  const openReceiveDialog = async (transferId) => {
    try {
      setError(null);
      // Backend returns { success, transfer } (same unwrap as loadTransferDetails)
      const res = await getTransfer(transferId);
      const detail = res?.transfer || res?.data || res;
      const items = detail.items || [];
      setReceiveTarget(detail);
      setReceiveLines(items.map(it => ({
        item_id: it.id,
        sku: it.sku,
        batch_number: it.batch_number,
        quantity_shipped: Number(it.quantity_shipped) || 0,
        quantity_received: Number(it.quantity_shipped) || 0,
        discrepancy_reason: '',
      })));
      setReceiveMeta({
        received_date: new Date().toISOString().slice(0, 10),
        keep_in_transit: false,
      });
      setReceiveDialogOpen(true);
    } catch (err) {
      setError(extractApiError(err) || 'Failed to open receive dialog');
    }
  };

  const submitReceive = async () => {
    if (!receiveTarget) return;
    // Validate: any line with received < shipped must have a reason
    const missingReason = receiveLines.find(l =>
      Number(l.quantity_received) < Number(l.quantity_shipped) && !l.discrepancy_reason.trim()
    );
    if (missingReason) {
      setError(`SKU ${missingReason.sku}: discrepancy reason required when received < shipped.`);
      return;
    }
    try {
      setReceiveSaving(true);
      const body = {
        received_date: receiveMeta.received_date || undefined,
        keep_in_transit: !!receiveMeta.keep_in_transit,
        items: receiveLines.map(l => ({
          item_id: l.item_id,
          quantity_received: Number(l.quantity_received) || 0,
          discrepancy_reason: l.discrepancy_reason || undefined,
        }))
      };
      setWarning(null);
      const received = await receiveTransfer(receiveTarget.id, body);
      setWarning(skippedCostRowsWarning(skippedCostRows(received), `Transfer ${receiveTarget.transfer_number} received`));
      const lossLines = receiveLines.filter(l => Number(l.quantity_received) < Number(l.quantity_shipped));
      const newStatus = body.keep_in_transit ? 'in_transit' : 'completed';
      setSuccess(`Received transfer ${receiveTarget.transfer_number}${lossLines.length ? ` — ${lossLines.length} line(s) with discrepancy` : ''}.`);
      setReceiveDialogOpen(false);
      const now = new Date().toISOString();
      if (selectedTransfer && selectedTransfer.id === receiveTarget.id) {
        setSelectedTransfer(prev => ({ ...prev, status: newStatus, received_at: now }));
      }
      setTransfers(prev => prev.map(t => t.id === receiveTarget.id ? { ...t, status: newStatus } : t));
    } catch (err) {
      setError(extractApiError(err) || 'Failed to receive');
    } finally { setReceiveSaving(false); }
  };

  const handleReceiveTransfer = (transferId) => openReceiveDialog(transferId);

  const handleDeleteTransfer = async () => {
    try {
      setError(null);
      const deletedId = deleteTargetId;
      await deleteTransfer(deletedId);
      setSuccess('Transfer deleted successfully');
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      setSelectedTransfer(null);
      // Track deleted ID so re-fetches from ClickHouse also exclude it (async mutations)
      recentlyDeletedIds.current.add(deletedId);
      setTransfers(prev => prev.filter(t => t.id !== deletedId));
      navigate('/inventory/transfers');
    } catch (err) {
      setError(extractApiError(err) || 'Failed to delete transfer');
      setDeleteConfirmOpen(false);
    }
  };

  const getStatusStep = (status) => {
    switch (status) {
      case 'draft': return 0;
      case 'in_transit': return 1;
      case 'completed': return 2;
      default: return 0;
    }
  };

  // Rendered in BOTH the detail and list return blocks (only one mounts at a
  // time). The shared ConfirmDialog keeps pre-line, so the \n\n paragraph
  // breaks in the messages above still render as paragraphs.
  const confirmDialog = <ConfirmDialog {...confirmDialogProps} />;

  // Full-page spinner ONLY on first ever load. Subsequent refetches keep the
  // UI mounted so open Select/Autocomplete dropdowns don't close mid-flick. A transfer opened by URL keeps the spinner
  // until its own answer lands; the list shows only if that load fails (DT-1113). The detail view shows only the transfer
  // the URL names: after moving from one transfer to another the previous one is still in selectedTransfer.
  const urlTransferShown = Boolean(id && selectedTransfer && String(selectedTransfer.id) === String(id));
  if ((loading && !initialLoaded) || (id && !urlTransferShown && detailSettledId !== String(id))) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress aria-label={id ? 'Loading transfer' : 'Loading transfers'} />
      </Box>
    );
  }

  // Transfer Detail View
  if (urlTransferShown) {
    const fromWh = warehouses.find(w => w.code === selectedTransfer.from_warehouse_code);
    const toWh   = warehouses.find(w => w.code === selectedTransfer.to_warehouse_code);
    const crossBorder = fromWh && toWh && fromWh.country && toWh.country && fromWh.country !== toWh.country;
    const fromLabel = fromWh?.name || selectedTransfer.from_warehouse_code;
    const toLabel   = toWh?.name   || selectedTransfer.to_warehouse_code;
    // Back → the filtered list the operator came from (falls back to the plain
    // list on a deep-link with no saved filters). See the sessionStorage effect.
    let savedListSearch = '';
    try { savedListSearch = sessionStorage.getItem('scm.transfersListSearch') || ''; } catch (_) { savedListSearch = ''; }
    const listBackTo = `/inventory/transfers${savedListSearch}`;
    return (
      <Box>
        <PageHeader
          backTo={listBackTo}
          primary={`${fromLabel} → ${toLabel}`}
          slug={selectedTransfer.transfer_number}
          metadata={[
            ...(selectedTransfer.transfer_date ? [{ label: `Transfer date ${formatDate(selectedTransfer.transfer_date)}` }] : []),
            ...(crossBorder ? [{ label: `Cross-border (${fromWh.country} → ${toWh.country})` }] : []),
          ]}
          statusBadge={
            // 'draft' displays as "Open" (DB value unchanged). Rendered as a
            // plain Chip so the shared StatusBadge (used app-wide) isn't touched;
            // color 'default' matches StatusBadge's mapping for draft.
            selectedTransfer.status === 'draft'
              ? <Chip label="Open" size="medium" sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
              : <StatusBadge status={selectedTransfer.status} size="medium" />
          }
          actions={
            <ActionBar
              primaryCount={1}
              actions={[
                {
                  // Commercial Invoice / Packing List always available — operators
                  // need the doc for forwarders regardless of cross-border status
                  // (same-country freight still wants paperwork) AND the crossBorder
                  // gate gave false negatives when warehouse country fields were
                  // sparse (e.g. country='EU' instead of an ISO code). Doc itself
                  // tolerates missing geography fields.
                  key: 'commercial-invoice',
                  label: 'Commercial Invoice',
                  icon: <DescriptionIcon fontSize="small" />,
                  onClick: async () => {
                    // Confirm before issuing — the CI is a customs document
                    // and operators were hitting this action accidentally.
                    // generateFromTransfer is idempotent (returns existing CI
                    // if one is linked to this transfer) so the dialog also
                    // surfaces that the second click is a no-op + view, not
                    // a re-issue. Reported by Rishabh 2026-05-21.
                    const ok = await confirm({
                      title: 'Issue a Commercial Invoice?',
                      message:
                        `For transfer ${selectedTransfer.transfer_number || selectedTransfer.id}. Your forwarder and customs broker work from this document.\n\n`
                        + 'If this transfer already has a commercial invoice it is reused, not duplicated. You can still edit it afterwards from the Commercial Invoices page.',
                      confirmText: 'Issue invoice',
                      severity: 'info',
                    });
                    if (!ok) return;
                    try {
                      const r = await api.post(`/api/commercial-invoices/from-transfer/${selectedTransfer.id}`);
                      setSuccess(`Commercial invoice ${r.data?.data?.invoice_number || r.data?.data?.id}: ${r.data?.data?.reused ? 'reused existing' : 'created'}`);
                      setTimeout(() => navigate('/inventory/commercial-invoices'), 800);
                    } catch (e) {
                      setError(e.response?.data?.error || e.message);
                    }
                  },
                },
                { key: 'print', label: 'Print', icon: <PrintIcon fontSize="small" />, onClick: () => window.print() },
                { key: 'copy', label: 'Copy transfer #', icon: <CopyIcon fontSize="small" />, onClick: () => navigator.clipboard?.writeText(selectedTransfer.transfer_number || '') },
              ]}
            />
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {warning && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
            {warning}
          </Alert>
        )}

        {/* Status Stepper */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stepper activeStep={getStatusStep(selectedTransfer.status)} alternativeLabel>
              {STATUS_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
        {/* Shipment costs — READ-ONLY. Mirrors the PO Shipments card, but fed
            entirely from the transfer's OWN freight/customs (entered on the edit
            page → Shipping & Landed Cost) allocated BY VALUE across the lines.
            No separate shipment record and no create/edit here — it just shows
            what was fed on the edit page. (Reverted the prior "Add shipment"
            sub-system per Rishabh: the overview should display, not create.) */}
        {(() => {
          const items = (selectedTransfer.items || []).filter((it) => it.sku);
          const freight = Number(selectedTransfer.freight_cost) || 0;
          const customs = Number(selectedTransfer.customs_duty) || 0;
          const ccy = selectedTransfer.currency || 'USD';
          const valuedRaw = items.map((it) => {
            const qty = Number(it.quantity_shipped) || Number(it.quantity_requested) || 0;
            const unit = Number(it.unit_cost) || 0;
            return { sku: it.sku, qty, unit, value: qty * unit };
          });
          // DISPLAY AGGREGATION (Rishabh): a SKU is stored as one item row PER
          // SOURCE BATCH (FIFO split in createWarehouseTransfer). Collapse to ONE
          // row per SKU for display — the per-batch rows still exist underneath
          // for cost tracking. Every value below is linear in qty/value, so the
          // weighted unit (value/qty) and the Totals row are byte-identical to
          // summing the per-batch rows.
          const bySkuValued = new Map();
          for (const r of valuedRaw) {
            const cur = bySkuValued.get(r.sku) || { sku: r.sku, qty: 0, value: 0, batches: 0 };
            cur.qty += r.qty;
            cur.value += r.value;
            cur.batches += 1;
            bySkuValued.set(r.sku, cur);
          }
          const valued = Array.from(bySkuValued.values()).map((a) => ({
            sku: a.sku, qty: a.qty, value: a.value,
            unit: a.qty > 0 ? a.value / a.qty : 0,
            batches: a.batches,
          }));
          const totalValue = valued.reduce((s, r) => s + r.value, 0);
          const rows = valued.map((r) => {
            const share = totalValue > 0 ? r.value / totalValue : 0;
            const allocFreight = freight * share;
            const allocDuty = customs * share;
            const landedUnit = r.qty > 0 ? r.unit + (allocFreight + allocDuty) / r.qty : r.unit;
            return { ...r, allocFreight, allocDuty, landedUnit };
          });
          const supplierTotal = totalValue;
          const allocatedTotal = freight + customs;
          const landedTotal = supplierTotal + allocatedTotal;
          return (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6" fontWeight="600">Shipment costs</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Landed cost overhead: <strong>{formatShipCurrency(allocatedTotal, ccy)}</strong>
                  </Typography>
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Freight</Typography>
                    <Typography variant="body1" fontWeight={600}>{formatShipCurrency(freight, ccy)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Customs duty</Typography>
                    <Typography variant="body1" fontWeight={600}>{formatShipCurrency(customs, ccy)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Allocation</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedTransfer.landed_cost_method || selectedTransfer.allocation_method || 'by value'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Incoterms</Typography>
                    <Typography variant="body1" fontWeight={600}>{selectedTransfer.incoterms || '—'}</Typography>
                  </Grid>
                </Grid>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Cost</TableCell>
                        <TableCell align="right">Alloc. Freight</TableCell>
                        <TableCell align="right">Alloc. Duty</TableCell>
                        <TableCell align="right">Landed Unit Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="textSecondary">No line items on this transfer yet.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : rows.map((r) => (
                        <TableRow key={r.sku}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{r.sku}</Typography>
                            {r.batches > 1 && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: 10 }}>
                                {r.batches} batches combined
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{formatNumber(r.qty)}</TableCell>
                          <TableCell align="right">{formatShipCurrency(r.unit, ccy)}</TableCell>
                          <TableCell align="right">{formatShipCurrency(r.allocFreight, ccy)}</TableCell>
                          <TableCell align="right">{formatShipCurrency(r.allocDuty, ccy)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatShipCurrency(r.landedUnit, ccy)}</TableCell>
                        </TableRow>
                      ))}
                      {rows.length > 0 && (
                        <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 600 } }}>
                          <TableCell>Totals</TableCell>
                          <TableCell align="right" />
                          <TableCell align="right">{formatShipCurrency(supplierTotal, ccy)}</TableCell>
                          <TableCell align="right">{formatShipCurrency(freight, ccy)}</TableCell>
                          <TableCell align="right">{formatShipCurrency(customs, ccy)}</TableCell>
                          <TableCell align="right">{formatShipCurrency(landedTotal, ccy)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {freight === 0 && customs === 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5 }}>
                    No freight or customs entered yet — add them on the transfer's edit page (Shipping & Landed Cost) and they'll allocate across these SKUs automatically.
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Items — full requested/shipped/received/discrepancy breakdown */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Items</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell align="right">Requested</TableCell>
                    <TableCell align="right">Shipped</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Discrepancy</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Landed Cost</TableCell>
                    <TableCell align="right">Line Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Landed unit cost isn't stored per transfer item — compute it
                    // here the SAME way the Shipment costs card does (allocate the
                    // transfer's freight + customs across the lines BY VALUE), so the
                    // Items "Landed Cost" matches that card instead of rendering blank.
                    const shipFreight = Number(selectedTransfer.freight_cost) || 0;
                    const shipCustoms = Number(selectedTransfer.customs_duty) || 0;
                    // Match the Shipment costs card's denominator exactly: it filters
                    // out sku-less lines before summing the allocation base.
                    const itemsTotalValue = (selectedTransfer.items || []).filter((it) => it.sku).reduce((s, it) => {
                      const q = Number(it.quantity_shipped) || Number(it.quantity_requested) || 0;
                      return s + q * (Number(it.unit_cost) || 0);
                    }, 0);
                    // Compute every STORED (per-batch) row first — these per-batch
                    // numbers are the authoritative ones for cost tracking and stay
                    // untouched underneath. Landed cost per batch mirrors the prior
                    // per-row logic exactly (booked destination-batch cost, else
                    // freight+customs allocated by value).
                    const perBatch = (selectedTransfer.items || []).map((item) => {
                      const qReq = Number(item.quantity_requested) || 0;
                      const qShip = Number(item.quantity_shipped) || 0;
                      const qRec = Number(item.quantity_received) || 0;
                      const disc = Number(item.discrepancy_qty) || Math.max(0, qShip - qRec);
                      const cost = Number(item.unit_cost) || 0;
                      const booked = Number(item.landed_unit_cost) || 0;
                      const allocQty = qShip || qReq || 0;
                      const share = itemsTotalValue > 0 ? (allocQty * cost) / itemsTotalValue : 0;
                      const computed = allocQty > 0
                        ? cost + ((shipFreight + shipCustoms) * share) / allocQty
                        : cost;
                      const landed = booked > 0 ? booked : computed;
                      return {
                        sku: item.sku,
                        product_name: item.product_name || item.description || '',
                        batch_number: item.batch_number || '',
                        qReq, qShip, qRec, disc, cost, booked, allocQty, landed,
                        lineValue: qShip * cost,
                        discrepancy_reason: item.discrepancy_reason || '',
                      };
                    });
                    // DISPLAY AGGREGATION (Rishabh): a SKU is stored as one row PER
                    // SOURCE BATCH (FIFO split in createWarehouseTransfer). Collapse
                    // to ONE row per SKU — quantities/line-value SUMMED; unit + landed
                    // shown WEIGHTED by allocation qty (so unit×qty reproduces the
                    // per-batch value exactly, and a single-batch SKU renders identical
                    // numbers to before). The per-batch rows still exist underneath;
                    // batch numbers are kept in the Batch cell tooltip so nothing that
                    // matters for cost/audit is hidden.
                    const bySku = new Map();
                    for (const b of perBatch) {
                      const cur = bySku.get(b.sku) || {
                        sku: b.sku, product_name: '', batches: [],
                        qReq: 0, qShip: 0, qRec: 0, disc: 0,
                        allocQty: 0, costValue: 0, landedValue: 0, lineValue: 0,
                        costSum: 0, landedSum: 0, n: 0, anyBooked: false, reasons: [],
                      };
                      if (!cur.product_name && b.product_name) cur.product_name = b.product_name;
                      cur.batches.push(b.batch_number);
                      cur.qReq += b.qReq; cur.qShip += b.qShip; cur.qRec += b.qRec; cur.disc += b.disc;
                      cur.allocQty += b.allocQty;
                      cur.costValue += b.allocQty * b.cost;
                      cur.landedValue += b.allocQty * b.landed;
                      cur.lineValue += b.lineValue;
                      cur.costSum += b.cost; cur.landedSum += b.landed; cur.n += 1;
                      if (b.booked > 0) cur.anyBooked = true;
                      if (b.disc > 0 && b.discrepancy_reason) cur.reasons.push(b.discrepancy_reason);
                      bySku.set(b.sku, cur);
                    }
                    const aggRows = Array.from(bySku.values()).map((a) => ({
                      ...a,
                      // Weighted by allocation qty; fall back to a plain mean only for
                      // the degenerate zero-qty line so its cost still shows.
                      unit: a.allocQty > 0 ? a.costValue / a.allocQty : (a.n > 0 ? a.costSum / a.n : 0),
                      landed: a.allocQty > 0 ? a.landedValue / a.allocQty : (a.n > 0 ? a.landedSum / a.n : 0),
                      batchList: a.batches.filter(Boolean),
                    }));
                    return aggRows.map((row, idx) => {
                    const disc = row.disc;
                    const batchCount = row.batchList.length;
                    const multiBatch = batchCount > 1;
                    return (
                      <TableRow key={row.sku || idx} sx={disc > 0 ? { bgcolor: 'error.lighter' } : {}}>
                        <TableCell><Typography fontWeight="medium">{row.sku}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">{row.product_name || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {batchCount === 0 ? '—' : multiBatch ? (
                            <Tooltip title={row.batchList.join(', ')} arrow>
                              <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>{batchCount} batches</span>
                            </Tooltip>
                          ) : row.batchList[0]}
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.qReq)}</TableCell>
                        <TableCell align="right">{formatNumber(row.qShip)}</TableCell>
                        <TableCell align="right">{formatNumber(row.qRec)}</TableCell>
                        <TableCell align="right" sx={{ color: disc > 0 ? 'error.main' : 'text.secondary', fontWeight: disc > 0 ? 600 : 400 }}>
                          {disc > 0 ? (
                            <Tooltip title={row.reasons.length ? Array.from(new Set(row.reasons)).join('; ') : '(no reason)'} arrow>
                              <span style={{ cursor: 'help', borderBottom: '1px dotted' }}>{disc}</span>
                            </Tooltip>
                          ) : '0'}
                        </TableCell>
                        <TableCell align="right">{row.unit.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {row.landed > 0 ? (
                            <Tooltip title={(row.anyBooked
                                ? 'Booked landed cost the units arrived at (destination batch)'
                                : 'Landed cost = base + freight/customs allocated by value')
                                + (multiBatch ? ' — blended across batches' : '')} arrow>
                              <span style={{ cursor: 'help', fontWeight: 600, borderBottom: '1px dotted' }}>{row.landed.toFixed(2)}</span>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right">{row.lineValue.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Transfer Info */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Route</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">From</Typography>
                    <EntityChip code={selectedTransfer.from_warehouse_code} name={fromWh?.name} />
                  </Box>
                  <ArrowIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">To</Typography>
                    <EntityChip code={selectedTransfer.to_warehouse_code} name={toWh?.name} />
                  </Box>
                </Box>
                {/* Originating PO(s) — set on transfers generated from (or
                    entered against) a Purchase Order, e.g. distributing a
                    manufacture PO's output from the manufacturer warehouse.
                    po_number can hold several space-joined POs — one link each. */}
                {splitPoNumbers(selectedTransfer.po_number).length > 0 && (
                  <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 1, mb: 1.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                      Source Purchase Order{splitPoNumbers(selectedTransfer.po_number).length > 1 ? 's' : ''}
                    </Typography>
                    {splitPoNumbers(selectedTransfer.po_number).map((po) => (
                      <Typography
                        key={po}
                        variant="body2"
                        role="link"
                        tabIndex={0}
                        onClick={() => navigate(`/procurement/purchase-orders?q=${encodeURIComponent(po)}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/procurement/purchase-orders?q=${encodeURIComponent(po)}`); } }}
                        sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {po}
                      </Typography>
                    ))}
                  </Box>
                )}
                {/* Subsidiary mapping — drives the commercial invoice */}
                {(selectedTransfer.from_subsidiary_name || selectedTransfer.to_subsidiary_name) && (
                  <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 1 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                      Invoicing entities
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontSize: 12 }}>
                      <strong>{selectedTransfer.from_subsidiary_name || '—'}</strong>
                      {selectedTransfer.from_subsidiary_country ? ` (${selectedTransfer.from_subsidiary_country})` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
                      → {selectedTransfer.to_subsidiary_name || '—'}
                      {selectedTransfer.to_subsidiary_country ? ` (${selectedTransfer.to_subsidiary_country})` : ''}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Dates</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Dates are DATE-DRIVEN: dispatch_date = sent, actual_arrival_date
                      = received. shipped_at/received_at are legacy DateTime mirrors
                      that get NULLed on ANY edit (service.js recreate path), so a
                      completed transfer that was edited would wrongly read
                      "In Transit" if we keyed Date Received off received_at alone
                      (Rishabh 2026-07-15). Prefer the date-driven fields; fall back
                      to the legacy timestamps. */}
                  {(() => {
                    const clean = (d) => (d && !String(d).startsWith('1970') && !String(d).startsWith('0000') ? d : null);
                    const sent = clean(selectedTransfer.dispatch_date) || clean(selectedTransfer.shipped_at);
                    const received = clean(selectedTransfer.actual_arrival_date) || clean(selectedTransfer.received_at);
                    if (sent || received) {
                      return (
                        <>
                          <Box>
                            <Typography variant="body2" color="textSecondary">Date Sent</Typography>
                            <Typography variant="body1">{sent ? formatDate(sent) : '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="textSecondary">Date Received</Typography>
                            <Typography variant="body1" component="div">
                              {received
                                ? formatDate(received)
                                : (selectedTransfer.status === 'completed'
                                    ? '—'
                                    : <Chip label="In Transit" color="warning" size="small" />)}
                            </Typography>
                          </Box>
                        </>
                      );
                    }
                    /* No logistics dates yet — show the transfer date. */
                    return (
                      <Box>
                        <Typography variant="body2" color="textSecondary">Transfer Date</Typography>
                        <Typography variant="body1">{formatDate(selectedTransfer.transfer_date)}</Typography>
                        {selectedTransfer.status === 'in_transit' && (
                          <Chip label="In Transit" color="warning" size="small" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actions</Typography>
                {/* Ship / Mark-as-Received buttons removed (Rishabh 2026-07-01):
                    status is DATE-DRIVEN — dispatch by setting the Dispatch Date
                    on Edit (→ In Transit), receive by setting Qty Transferred +
                    Completed Date on Edit (→ Completed). The old buttons were a
                    confusing, redundant second path. */}
                {(selectedTransfer.status === 'draft' || selectedTransfer.status === 'in_transit') && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {selectedTransfer.status === 'draft'
                      ? 'To dispatch: click Edit and set the Dispatch Date, then Save.'
                      : 'To receive: click Edit, set Qty Transferred + the Completed Date, then Save.'}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {/* Edit: all statuses except cancelled show edit */}
                  {selectedTransfer.status !== 'cancelled' && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/inventory/transfers/${selectedTransfer.id}/edit`)}
                    >
                      Edit
                    </Button>
                  )}
                  {/* Delete: all statuses */}
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => { setDeleteTargetId(selectedTransfer.id); setDeleteConfirmOpen(true); }}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Logistics + Notes summary */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Shipment</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Carrier</Typography>
                    <Typography variant="body2">{selectedTransfer.carrier || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Tracking #</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{selectedTransfer.tracking_number || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Transport Mode</Typography>
                    <Typography variant="body2">{selectedTransfer.transport_mode || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Expected Arrival</Typography>
                    <Typography variant="body2">{selectedTransfer.expected_arrival_date ? formatDate(selectedTransfer.expected_arrival_date) : '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Cartons</Typography>
                    <Typography variant="body2">{selectedTransfer.cartons || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Gross / Net Wt (kg)</Typography>
                    <Typography variant="body2">
                      {Number(selectedTransfer.gross_weight_kg) || 0} / {Number(selectedTransfer.net_weight_kg) || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Internal (ops only)</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{selectedTransfer.notes || <em style={{ opacity: 0.6 }}>none</em>}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>Customer-facing (on invoice / packing list)</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{selectedTransfer.customer_notes || <em style={{ opacity: 0.6 }}>none</em>}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Activity timeline — audit_log entries for this transfer (created /
            dispatched / received / edits / voids). Reuses the same DS Timeline +
            audit-log endpoint the PO overview uses. */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>Activity</Typography>
            <Timeline
              events={(transferTimelineEvents || []).map(e => ({
                ...e,
                // Semantic audit actions (create/ship/receive/...) come through raw;
                // map to past-tense to match the PO timeline's style ("<actor> created").
                action: ({ create: 'created', ship: 'dispatched', receive: 'received',
                           update: 'edited', delete: 'deleted', bulk_delete: 'deleted',
                           reverse_ship: 'reversed dispatch', reverse_receive: 'reversed receipt',
                           cancel: 'voided' }[String(e.action || '').toLowerCase()]) || e.action,
                timestamp: e.timestamp ? formatDate(e.timestamp) + ' ' + (String(e.timestamp).split(' ')[1] || '').slice(0, 5) : '',
              }))}
              loading={transferTimelineLoading}
              empty={
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No activity recorded yet.
                </Typography>
              }
            />
          </CardContent>
        </Card>

        {/* Document actions — Issue Commercial Invoice + Packing List PDF */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>Documents</Typography>
                <Typography variant="body2" color="textSecondary">
                  Commercial invoice uses <strong>{selectedTransfer.from_subsidiary_name || '—'}</strong> as seller and
                  <strong> {selectedTransfer.to_subsidiary_name || '—'}</strong> as consignee, sourced from the subsidiary mapping above.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" startIcon={<DescriptionIcon />}
                  onClick={async () => {
                    try {
                      const tnum = encodeURIComponent(selectedTransfer.transfer_number || '');
                      const pdfResp = await api.get(`/api/warehouse-transfers/${selectedTransfer.id}/pdf?transfer_number=${tnum}`, { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                      const a = document.createElement('a'); a.href = url;
                      a.download = `TransferOrder-${selectedTransfer.transfer_number}.pdf`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (e) { setError(e.response?.data?.error || e.message); }
                  }}>
                  Transfer Order (PDF)
                </Button>
                <Button variant="contained" startIcon={<DescriptionIcon />}
                  onClick={async () => {
                    try {
                      const r = await api.post(`/api/commercial-invoices/from-transfer/${selectedTransfer.id}`);
                      const ciId = r?.data?.data?.id;
                      if (ciId) {
                        setSuccess(`Commercial invoice ${ciId} created — opening PDF…`);
                        const pdfResp = await api.get(`/api/commercial-invoices/${ciId}/pdf?kind=invoice`, { responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                        const a = document.createElement('a'); a.href = url;
                        a.download = `CommercialInvoice-${selectedTransfer.transfer_number}.pdf`;
                        document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } else setError('Commercial invoice generation returned no id');
                    } catch (e) { setError(e.response?.data?.error || e.message); }
                  }}>
                  Issue Commercial Invoice (PDF)
                </Button>
                <Button variant="outlined" startIcon={<DescriptionIcon />}
                  onClick={async () => {
                    try {
                      // Generate the invoice first (idempotent) so packing list shares the same id.
                      const r = await api.post(`/api/commercial-invoices/from-transfer/${selectedTransfer.id}`);
                      const ciId = r?.data?.data?.id;
                      if (!ciId) { setError('Could not resolve commercial invoice id'); return; }
                      const pdfResp = await api.get(`/api/commercial-invoices/${ciId}/pdf?kind=packing-list`, { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                      const a = document.createElement('a'); a.href = url;
                      a.download = `PackingList-${selectedTransfer.transfer_number}.pdf`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    } catch (e) { setError(e.response?.data?.error || e.message); }
                  }}>
                  Issue Packing List (PDF)
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Attachments — operator-uploaded files on this transfer (PDF/CSV/XLSX/PNG/JPG) */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: attachments.length ? 2 : 0 }}>
              <Box>
                <Typography variant="h6" gutterBottom>Attachments</Typography>
                <Typography variant="body2" color="textSecondary">
                  Supplier paperwork, packing lists, carrier files — PDF, CSV, XLSX, PNG or JPG, up to 25&nbsp;MB.
                </Typography>
                {safeHref(selectedTransfer.drive_link) && (
                  <Typography
                    variant="body2"
                    component="a"
                    href={safeHref(selectedTransfer.drive_link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'inline-block', mt: 0.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    📎 Google Drive link ↗
                  </Typography>
                )}
              </Box>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />} disabled={attachmentBusy}>
                {attachmentBusy ? 'Uploading…' : 'Upload file'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.csv,.xlsx,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    handleUploadAttachment(selectedTransfer.id, e.target.files?.[0]);
                    e.target.value = ''; // allow re-selecting the same file
                  }}
                />
              </Button>
            </Box>
            {attachments.length > 0 && (
              <Table size="small">
                <TableBody>
                  {attachments.map((f) => (
                    <TableRow key={f.filename}>
                      <TableCell sx={{ width: 28, pr: 0 }}><AttachFileIcon fontSize="small" color="action" /></TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => handleDownloadAttachment(selectedTransfer.id, f.filename)}
                        >
                          {f.filename}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {(f.size / 1024).toFixed(0)} KB · {formatDate(f.uploaded_at)}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 48 }}>
                        <IconButton size="small" onClick={() => handleDeleteAttachment(selectedTransfer.id, f.filename)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Delete Transfer</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete transfer <strong>{selectedTransfer.transfer_number}</strong>?
            </Typography>
            {(selectedTransfer.status === 'in_transit' || selectedTransfer.status === 'completed') && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This transfer is <strong>{selectedTransfer.status === 'in_transit' ? 'in transit' : 'completed'}</strong>.
                Deleting it will reverse all inventory changes
                {selectedTransfer.status === 'completed' && ' (source batches restored, destination batches removed)'}.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDeleteTransfer}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Generic confirm dialog (attachment delete, commercial-invoice issue) */}
        {confirmDialog}

      </Box>
    );
  }

  // Transfer List View
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Warehouse Transfers
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage inter-warehouse stock transfers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/inventory/transfers/new')}
          >
            New Transfer
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTransfers}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      {/* Filters — search + multi-checkbox status + multi-checkbox From / To
          warehouses + date range. Mirrors the PO list filter card (Rishabh
          request 2026-05-22) so the two modules feel consistent. statusFilter,
          fromWhFilter, toWhFilter are comma-separated lists in state; URL
          round-trips through useUrlState. */}
      <Card sx={{ mb: 2, position: 'relative' }}>
        {/* Refetch progress strip — same pattern as PO. Surfaces "loading"
            without unmounting the dropdowns above the table (Rishabh fix
            2026-05-22 — ticking one checkbox closed the open menu). */}
        {loading && initialLoaded && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
          />
        )}
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                placeholder="Search transfer #, PO #, tracking, carrier, notes…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter commits immediately (skip the 400ms debounce); Esc clears.
                  if (e.key === 'Enter') setSearchTerm(searchInput);
                  if (e.key === 'Escape') { setSearchInput(''); setSearchTerm(''); }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={1.75}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter ? statusFilter.split(',').filter(Boolean) : []}
                  label="Status"
                  onChange={(e) => {
                    const arr = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    // Handle the __ALL__ sentinel here (not on the MenuItem's onClick) — MUI Select's
                    // onChange fires AFTER the MenuItem onClick and clobbers the setState the MenuItem
                    // performs, so the clear/select never sticks. Intercepting in onChange is the
                    // single source of truth for the toggle.
                    if (arr.includes('__ALL__')) {
                      const all = ['draft', 'in_transit', 'completed', 'cancelled'];
                      const cur = statusFilter ? statusFilter.split(',').filter(Boolean) : [];
                      setStatusFilter(cur.length > 0 ? '' : all.join(','));
                      return;
                    }
                    setStatusFilter(arr.filter(v => v && v !== '__ALL__').join(','));
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All';
                    // 'draft' shows as "Open" (filter VALUE stays 'draft').
                    if (selected.length === 1) return selected[0] === 'draft' ? 'Open' : selected[0].replace(/_/g, ' ');
                    return `${selected.length} selected`;
                  }}
                >
                  {/* Select all / Clear all — Rishabh request 2026-05-22.
                      Label flips based on whether *any* statuses are selected — clicking when
                      one or more are picked clears them; clicking when none are picked selects all. */}
                  <MenuItem
                    value="__ALL__"
                    sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={(statusFilter ? statusFilter.split(',').filter(Boolean) : []).length > 0 ? 'Clear all' : 'Select all'}
                      primaryTypographyProps={{ sx: { fontSize: 13, color: 'primary.main' } }}
                    />
                  </MenuItem>
                  {['draft', 'in_transit', 'completed', 'cancelled'].map(s => {
                    const selectedArr = statusFilter ? statusFilter.split(',').filter(Boolean) : [];
                    return (
                      <MenuItem key={s} value={s} sx={{ py: 0.5 }}>
                        <Checkbox checked={selectedArr.includes(s)} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText
                          primary={s === 'draft' ? 'Open' : s.replace(/_/g, ' ')}
                          primaryTypographyProps={{ sx: { textTransform: 'capitalize', fontSize: 13 } }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {/* Brand multi-checkbox filter (Rishabh request 2026-07-03).
                '__ALL__' sentinel handled in onChange — NOT a MenuItem
                onClick, which races MUI's own onChange (same fix as the PO
                Brand filter, 2026-07-03). '0' = Unassigned. */}
            <Grid item xs={6} md={1.75}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select
                  multiple
                  value={brandFilter ? brandFilter.split(',').filter(Boolean) : []}
                  label="Brand"
                  onChange={(e) => {
                    const arr = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    if (arr.includes('__ALL__')) {
                      const all = ['0', ...(brands || []).map(b => String(b.id))];
                      const cur = brandFilter ? brandFilter.split(',').filter(Boolean) : [];
                      setBrandFilter(cur.length === all.length ? '' : all.join(','));
                      return;
                    }
                    setBrandFilter(arr.filter(v => v && v !== '__ALL__').join(','));
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) return 'All';
                    if (selected.length === 1) {
                      if (selected[0] === '0') return 'Unassigned';
                      const b = (brands || []).find(x => String(x.id) === selected[0]);
                      return b?.name || `Brand #${selected[0]}`;
                    }
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                >
                  <MenuItem
                    value="__ALL__"
                    sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={(brandFilter ? brandFilter.split(',').filter(Boolean) : []).length === (1 + (brands || []).length) ? 'Clear all' : 'Select all'}
                      primaryTypographyProps={{ sx: { fontSize: 13, color: 'primary.main' } }}
                    />
                  </MenuItem>
                  <MenuItem key="0" value="0" sx={{ py: 0.5 }}>
                    <Checkbox
                      checked={(brandFilter ? brandFilter.split(',').filter(Boolean) : []).includes('0')}
                      size="small" sx={{ p: 0.5, mr: 1 }}
                    />
                    <ListItemText
                      primary="Unassigned"
                      primaryTypographyProps={{ sx: { fontStyle: 'italic', fontSize: 13, color: 'text.secondary' } }}
                    />
                  </MenuItem>
                  {[...(brands || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })).map(b => {
                    const selectedArr = brandFilter ? brandFilter.split(',').filter(Boolean) : [];
                    return (
                      <MenuItem key={b.id} value={String(b.id)} sx={{ py: 0.5 }}>
                        <Checkbox checked={selectedArr.includes(String(b.id))} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <ListItemText primary={b.name} primaryTypographyProps={{ sx: { fontSize: 13 } }} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {/* From warehouse — multi-checkbox Autocomplete with typeahead.
                Rishabh's "route-based" requirement: lets operators see all
                transfers OUT of a particular origin in one click. */}
            <Grid item xs={6} md={2}>
              {(() => {
                const selectedCodes = fromWhFilter ? fromWhFilter.split(',').filter(Boolean) : [];
                const selectedObjs = (warehouses || []).filter(w => selectedCodes.includes(w.code));
                return (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={(warehouses || []).slice().sort((a, b) => String(a.code).localeCompare(String(b.code)))}
                    value={selectedObjs}
                    onChange={(_, picked) => {
                      const codes = (picked || []).map(p => p.code).filter(Boolean);
                      setFromWhFilter(codes.length ? codes.join(',') : '');
                    }}
                    getOptionLabel={(opt) => opt?.code || ''}
                    filterOptions={warehouseFilter}
                    isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
                    PaperComponent={({ children, ...rest }) => {
                      // Sticky "Select all / Clear all" header at the top of
                      // the popper (Rishabh request 2026-05-22).
                      const allCodes = (warehouses || []).map(w => w.code).filter(Boolean);
                      const cur = fromWhFilter ? fromWhFilter.split(',').filter(Boolean) : [];
                      const allSelected = cur.length === allCodes.length && allCodes.length > 0;
                      return (
                        <Paper {...rest}>
                          <Box
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setFromWhFilter(allSelected ? '' : allCodes.join(','));
                            }}
                            sx={{ py: 0.75, px: 1.5, cursor: 'pointer', fontSize: 13, color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            {allSelected ? 'Clear all' : 'Select all'}
                          </Box>
                          {children}
                        </Paper>
                      );
                    }}
                    renderOption={(props, opt, { selected }) => (
                      <li {...props} key={opt.id || opt.code}>
                        <Checkbox checked={selected} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: 13 }}>{opt.code}</Typography>
                          {opt.name && (
                            <Typography variant="caption" color="text.secondary">{opt.name}</Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderTags={(value, getTagProps) => {
                      if (value.length === 1) {
                        return <Chip {...getTagProps({ index: 0 })} key={value[0].code} label={value[0].code} size="small" />;
                      }
                      return <Chip {...getTagProps({ index: 0 })} key="multi" label={`${value.length} selected`} size="small" />;
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="From warehouse" placeholder={selectedCodes.length ? '' : 'All'} />
                    )}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={6} md={2}>
              {(() => {
                const selectedCodes = toWhFilter ? toWhFilter.split(',').filter(Boolean) : [];
                const selectedObjs = (warehouses || []).filter(w => selectedCodes.includes(w.code));
                return (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={(warehouses || []).slice().sort((a, b) => String(a.code).localeCompare(String(b.code)))}
                    value={selectedObjs}
                    onChange={(_, picked) => {
                      const codes = (picked || []).map(p => p.code).filter(Boolean);
                      setToWhFilter(codes.length ? codes.join(',') : '');
                    }}
                    getOptionLabel={(opt) => opt?.code || ''}
                    filterOptions={warehouseFilter}
                    isOptionEqualToValue={(opt, val) => opt?.code === val?.code}
                    PaperComponent={({ children, ...rest }) => {
                      const allCodes = (warehouses || []).map(w => w.code).filter(Boolean);
                      const cur = toWhFilter ? toWhFilter.split(',').filter(Boolean) : [];
                      const allSelected = cur.length === allCodes.length && allCodes.length > 0;
                      return (
                        <Paper {...rest}>
                          <Box
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setToWhFilter(allSelected ? '' : allCodes.join(','));
                            }}
                            sx={{ py: 0.75, px: 1.5, cursor: 'pointer', fontSize: 13, color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            {allSelected ? 'Clear all' : 'Select all'}
                          </Box>
                          {children}
                        </Paper>
                      );
                    }}
                    renderOption={(props, opt, { selected }) => (
                      <li {...props} key={opt.id || opt.code}>
                        <Checkbox checked={selected} size="small" sx={{ p: 0.5, mr: 1 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: 13 }}>{opt.code}</Typography>
                          {opt.name && (
                            <Typography variant="caption" color="text.secondary">{opt.name}</Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderTags={(value, getTagProps) => {
                      if (value.length === 1) {
                        return <Chip {...getTagProps({ index: 0 })} key={value[0].code} label={value[0].code} size="small" />;
                      }
                      return <Chip {...getTagProps({ index: 0 })} key="multi" label={`${value.length} selected`} size="small" />;
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="To warehouse" placeholder={selectedCodes.length ? '' : 'All'} />
                    )}
                  />
                );
              })()}
            </Grid>
            <Grid item xs={6} md={1.25}>
              <TextField
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={1.25}>
              <TextField
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            {/* Actions row — Go + Export side by side on its own row so neither
                gets clipped by the filter columns above. Bulk actions appear
                here too when rows are ticked. */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="outlined" size="small" onClick={loadTransfers} disabled={bulkBusy}>Go</Button>
                <Button variant="outlined" size="small" onClick={handleExport} disabled={exporting || bulkBusy}>
                  {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
                {selectedIds.size > 0 && (
                  <>
                    <Button variant="outlined" color="warning" size="small" onClick={bulkCancelSelected} disabled={bulkBusy}>
                      {bulkBusy ? 'Working…' : `Cancel selected (${selectedIds.size})`}
                    </Button>
                    <Button variant="text" size="small" onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}>
                      Clear selection
                    </Button>
                  </>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {/* "Delete all cancelled" — always visible (operator's most
                    common bulk-cleanup ask). Backend uses the current filters
                    so they can scope by warehouse / date / search if desired. */}
                <Button variant="outlined" color="error" size="small" onClick={bulkDeleteAllCancelled} disabled={bulkBusy}>
                  Delete all cancelled
                  {statusFilter || fromWhFilter || toWhFilter || searchTerm || dateFromFilter || dateToFilter
                    ? ' (in filter)' : ''}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transfer Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* Bulk-select header. The indeterminate state shows up when
                    some — but not all — visible rows are selected, so the
                    operator can see "some ticked" at a glance. */}
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={transfers.length > 0 && selectedIds.size === transfers.length}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < transfers.length}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Transfer #</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>From → To Subsidiary</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Expected Arrival</TableCell>
                <TableCell>Carrier / Tracking</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Filters are applied server-side (status / from_wh / to_wh /
                // search / date range all flow into the GET /api/warehouse-transfers
                // params). The list response is the already-filtered set; no
                // client-side narrowing here.
                const filtered = transfers;
                if (filtered.length === 0) return (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box sx={{ py: 4 }}>
                      <TransferIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="textSecondary">
                        No transfers match the current filters
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                );
                return filtered.map((transfer) => (
                  <TableRow key={transfer.id} hover selected={selectedIds.has(Number(transfer.id))}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(Number(transfer.id))}
                        onChange={() => toggleSelectOne(transfer.id)}
                        // Avoid the row click bubbling — row click would still
                        // navigate to detail otherwise (currently the View button
                        // is the navigation surface so this is just defensive).
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => { e.stopPropagation(); navigate(`/inventory/transfers/${transfer.id}`); }}
                        sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {transfer.transfer_number}
                      </Box>
                      {/* Originating PO(s) (e.g. a manufacture PO whose output
                          this transfer distributes) — the column has existed on
                          the transfer since 2026-07-01 but was never displayed
                          (Rishabh 2026-07-17: no way to tell which PO generated
                          the NBC transfers). po_number can hold SEVERAL
                          space-joined POs (TRF-1077), so link each one
                          individually — a combined ?q= matches nothing. */}
                      {splitPoNumbers(transfer.po_number).map((po) => (
                        <Typography
                          key={po}
                          variant="caption"
                          role="link"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); navigate(`/procurement/purchase-orders?q=${encodeURIComponent(po)}`); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); navigate(`/procurement/purchase-orders?q=${encodeURIComponent(po)}`); } }}
                          sx={{ display: 'block', fontSize: 10, color: 'text.secondary', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                          PO {po}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EntityChip
                          code={transfer.from_warehouse_code}
                          name={warehouses.find(w => w.code === transfer.from_warehouse_code)?.name}
                        />
                        <ArrowIcon fontSize="small" color="action" />
                        <EntityChip
                          code={transfer.to_warehouse_code}
                          name={warehouses.find(w => w.code === transfer.to_warehouse_code)?.name}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: 11 }}>
                        {transfer.from_subsidiary_name || '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: 'text.secondary' }}>
                        → {transfer.to_subsidiary_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Hover reveals per-line SKU × qty (+ unit_cost when set)
                          for up to 50 lines. Server returns a pre-formatted
                          multi-line string in items_preview; whiteSpace=pre-line
                          preserves the newlines. */}
                      <Tooltip
                        arrow
                        placement="right"
                        title={
                          transfer.items_preview
                            ? <Box sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace', fontSize: 12 }}>{transfer.items_preview}</Box>
                            : 'No line items'
                        }
                      >
                        <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'divider', cursor: 'help' }}>
                          {transfer.item_count || 0} items
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(transfer.transfer_date)}</TableCell>
                    <TableCell>
                      {transfer.expected_arrival_date ? formatDate(transfer.expected_arrival_date) : '—'}
                    </TableCell>
                    <TableCell>
                      {transfer.carrier || transfer.tracking_number || safeHref(transfer.drive_link) ? (
                        <>
                          {transfer.carrier && <Typography variant="caption" sx={{ display: 'block', fontSize: 11 }}>{transfer.carrier}</Typography>}
                          {transfer.tracking_number && <Typography variant="caption" sx={{ display: 'block', fontSize: 10, fontFamily: 'monospace', color: 'text.secondary' }}>{transfer.tracking_number}</Typography>}
                          {safeHref(transfer.drive_link) && (
                            <Typography
                              variant="caption"
                              component="a"
                              href={safeHref(transfer.drive_link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ display: 'block', fontSize: 10, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              📎 Drive link
                            </Typography>
                          )}
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transfer.status === 'draft' ? 'Open' : transfer.status}
                        size="small"
                        color={STATUS_COLORS[transfer.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => navigate(`/inventory/transfers/${transfer.id}`)}
                      >
                        View
                      </Button>
                      {transfer.status === 'draft' && (
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleShipTransfer(transfer.id)}
                        >
                          Ship
                        </Button>
                      )}
                      {transfer.status === 'in_transit' && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => handleReceiveTransfer(transfer.id)}
                        >
                          Receive
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[50, 100, 200]}
          labelRowsPerPage="Transfers per page"
        />
      </Card>

      {/* Ship dialog — captures per-line quantity_shipped + actual ship date,
          tracking, carrier, cartons. Defaults: each line ships at qty_requested;
          operator can lower per line to record damage/loss before pickup. */}
      <Dialog open={shipDialogOpen} onClose={() => setShipDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ship Transfer {shipTarget?.transfer_number || ''}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {shipTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Shipping releases inventory from <strong>{shipTarget.from_warehouse_code}</strong> by the actual <em>Qty Shipped</em> below. If you ship less than requested on any line, the difference stays at source — record the reason in Internal Notes if needed.
              </Alert>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" type="date" label="Ship Date"
                    value={shipMeta.shipped_date}
                    onChange={(e) => setShipMeta({ ...shipMeta, shipped_date: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Carrier"
                    value={shipMeta.carrier}
                    onChange={(e) => setShipMeta({ ...shipMeta, carrier: e.target.value })}
                    placeholder="DHL / Maersk / DSV …" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Tracking #"
                    value={shipMeta.tracking_number}
                    onChange={(e) => setShipMeta({ ...shipMeta, tracking_number: e.target.value })} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Cartons Shipped" type="number"
                    value={shipMeta.cartons_shipped}
                    onChange={(e) => setShipMeta({ ...shipMeta, cartons_shipped: e.target.value })}
                    inputProps={{ min: 0 }} />
                </Grid>
              </Grid>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Line items — confirm or adjust shipped quantities</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell align="right">Requested</TableCell>
                      <TableCell align="right">Shipping Now</TableCell>
                      <TableCell align="right">Shortfall</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shipLines.map((l, idx) => {
                      const short = Number(l.quantity_requested) - Number(l.quantity_shipped);
                      return (
                        <TableRow key={l.item_id} sx={short > 0 ? { bgcolor: 'warning.lighter' } : {}}>
                          <TableCell>{l.sku}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{l.batch_number || '—'}</TableCell>
                          <TableCell align="right">{l.quantity_requested}</TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number"
                              value={l.quantity_shipped}
                              onChange={(e) => {
                                const v = Math.max(0, Math.min(Number(e.target.value) || 0, Number(l.quantity_requested) || 0));
                                setShipLines(prev => prev.map((p, i) => i === idx ? { ...p, quantity_shipped: v } : p));
                              }}
                              inputProps={{ min: 0, max: l.quantity_requested, step: 1, style: { textAlign: 'right' } }}
                              sx={{ width: 100 }} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: short > 0 ? 'warning.dark' : 'text.secondary', fontWeight: short > 0 ? 600 : 400 }}>
                            {short > 0 ? `−${short}` : '0'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipDialogOpen(false)} disabled={shipSaving}>Cancel</Button>
          <Button onClick={submitShip} variant="contained" color="warning" startIcon={<ShippingIcon />} disabled={shipSaving}>
            {shipSaving ? 'Shipping…' : 'Confirm Ship'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive dialog — captures per-line quantity_received and discrepancy
          reason (mandatory when received < shipped). keep_in_transit lets the
          operator mark this as a partial receive (more arrivals expected). */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Receive Transfer {receiveTarget?.transfer_number || ''}</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {receiveTarget && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Receiving creates new batches at <strong>{receiveTarget.to_warehouse_code}</strong> for each received line. Shortfalls (received &lt; shipped) are recorded as discrepancies on the line — they stay visible in transfer history for loss reporting.
              </Alert>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={4}>
                  <TextField fullWidth size="small" type="date" label="Received Date"
                    value={receiveMeta.received_date}
                    onChange={(e) => setReceiveMeta({ ...receiveMeta, received_date: e.target.value })}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Receive Type</InputLabel>
                    <Select value={receiveMeta.keep_in_transit ? 'partial' : 'full'} label="Receive Type"
                      onChange={(e) => setReceiveMeta({ ...receiveMeta, keep_in_transit: e.target.value === 'partial' })}>
                      <MenuItem value="full">Complete — close this transfer</MenuItem>
                      <MenuItem value="partial">Partial — more arrivals expected (keep in transit)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Line items — confirm received quantities</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell align="right">Shipped</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell>Discrepancy Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receiveLines.map((l, idx) => {
                      const loss = Number(l.quantity_shipped) - Number(l.quantity_received);
                      const needsReason = loss > 0;
                      return (
                        <TableRow key={l.item_id} sx={loss > 0 ? { bgcolor: 'error.lighter' } : {}}>
                          <TableCell>{l.sku}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{l.batch_number || '—'}</TableCell>
                          <TableCell align="right">{l.quantity_shipped}</TableCell>
                          <TableCell align="right">
                            <TextField size="small" type="number"
                              value={l.quantity_received}
                              onChange={(e) => {
                                const v = Math.max(0, Number(e.target.value) || 0);
                                setReceiveLines(prev => prev.map((p, i) => i === idx ? { ...p, quantity_received: v } : p));
                              }}
                              inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
                              sx={{ width: 100 }} />
                            {loss > 0 && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'error.main', fontWeight: 600, fontSize: 10 }}>
                                lost {loss}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 240 }}>
                            <TextField size="small" fullWidth
                              required={needsReason}
                              value={l.discrepancy_reason}
                              disabled={!needsReason}
                              onChange={(e) => {
                                const v = e.target.value;
                                setReceiveLines(prev => prev.map((p, i) => i === idx ? { ...p, discrepancy_reason: v } : p));
                              }}
                              placeholder={needsReason ? 'damage / theft / miscount …' : 'n/a — full receipt'}
                              error={needsReason && !l.discrepancy_reason.trim()}
                              helperText={needsReason && !l.discrepancy_reason.trim() ? 'required' : ''}
                              FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)} disabled={receiveSaving}>Cancel</Button>
          <Button onClick={submitReceive} variant="contained" color="success" startIcon={<CheckIcon />} disabled={receiveSaving}>
            {receiveSaving ? 'Receiving…' : (receiveMeta.keep_in_transit ? 'Confirm Partial Receive' : 'Confirm Receive')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generic confirm dialog (bulk cancel / delete / delete-all-cancelled) */}
      {confirmDialog}
    </Box>
  );
}

class TransfersErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Transfers crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            Something went wrong: {this.state.error?.message || 'Unknown error'}
          </Alert>
          <Button onClick={() => this.setState({ hasError: false, error: null })} sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

function TransfersWithBoundary() {
  return (
    <TransfersErrorBoundary>
      <Transfers />
    </TransfersErrorBoundary>
  );
}

export default TransfersWithBoundary;
